import { computed, inject, Service } from '@angular/core';
import { Feature, Permission } from '@core/models';
import { ContextStateService } from '@core/services/context-state.service';

export type ViewAllResponsesState = 'hidden' | 'visible' | 'disabled';

export type ResultsHubTab = 'analytics' | 'actions';

/**
 * Visibility rules for the module-level Results hub (Analytics / Actions tabs and "View all responses").
 * Shared by the route guards, the page header, the "View all responses" button and the main navigation.
 */
@Service()
export class ResultsHubAccessService {
    private readonly contextStateService = inject(ContextStateService);

    public readonly canViewAnalytics = computed(() => this.contextStateService.currentUserPermissions().includes(Permission.ViewAnalytics));

    /** Same rule as the main navigation's module-level Actions item. */
    public readonly canViewActions = computed(() => this.contextStateService.currentUserPermissions().includes(Permission.UseActions));

    public readonly canViewAnyTab = computed(() => this.canViewAnalytics() || this.canViewActions());

    public readonly defaultTab = computed<ResultsHubTab | null>(() => {
        if (this.canViewAnalytics()) {
            return 'analytics';
        }

        return this.canViewActions() ? 'actions' : null;
    });

    /** Same rules as the main navigation Results item: system admins always see it, disabled when the feature is off. */
    public readonly viewAllResponsesState = computed<ViewAllResponsesState>(() => {
        const hasResultsPageFeature = this.contextStateService.currentModuleFeatures().includes(Feature.ResultsPage);
        const currentUser = this.contextStateService.currentUser();

        if (currentUser?.isSystemAdministrator && currentUser.isImpersonating && this.contextStateService.currentOrganization()) {
            return hasResultsPageFeature ? 'visible' : 'disabled';
        }

        return this.contextStateService.currentUserPermissions().includes(Permission.ViewResults) && hasResultsPageFeature ? 'visible' : 'hidden';
    });

    public getResultsHubUrl(tab?: ResultsHubTab): string[] {
        const moduleSlug = this.contextStateService.selectedModuleSlug();

        return ['/', moduleSlug, 'results', ...(tab ? [tab] : [])];
    }

    public getResultsHubPath(tab: ResultsHubTab): string {
        return `/${this.contextStateService.selectedModuleSlug()}/results/${tab}`;
    }

    public getModuleHomeUrl(): string[] {
        return ['/', this.contextStateService.selectedModuleSlug(), 'home'];
    }

    public getAllResponsesUrl(): string[] {
        return ['/', this.contextStateService.selectedModuleSlug(), 'results', 'table'];
    }
}
